import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Gauge } from 'lucide-react';
import { cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@bliks-io/ui';
import type { CapacityBinding } from '@bliks-io/contracts';

export interface CapacityStatCardProps extends React.HTMLAttributes<HTMLDivElement> {
  capacity: CapacityBinding | null | undefined;
}

/**
 * CapacityStatCard - A specialized stat card for displaying capacity binding
 *
 * Features:
 * - Toggle between hours per month and hours per year
 * - Tooltip showing cost calculations
 * - Handles null/undefined gracefully
 */
function CapacityStatCard({
  capacity,
  className,
  ...props
}: CapacityStatCardProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [showYearly, setShowYearly] = React.useState(false);

  const hasData = capacity !== null && capacity !== undefined;

  // Map i18n language to locale for number/currency formatting
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';

  const displayValue = React.useMemo(() => {
    if (!hasData) return '-';
    const hours = showYearly ? capacity.hoursPerYear : capacity.hoursPerMonth;
    return `${hours.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h`;
  }, [hasData, capacity, showYearly, locale]);

  const periodLabel = showYearly
    ? t('organization.systems.stats.perYear')
    : t('organization.systems.stats.perMonth');

  const togglePeriod = () => setShowYearly((prev) => !prev);

  const formatCurrency = (value: number) => {
    return value.toLocaleString(locale, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const cardContent = (
    <div
      className={cn(
        'relative rounded-lg border bg-card p-5 shadow-xs',
        hasData && 'cursor-pointer hover:bg-accent/50 transition-colors',
        className
      )}
      onClick={hasData ? togglePeriod : undefined}
      {...props}
    >
      {/* Icon in top-right corner */}
      <div className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-md bg-info-surface">
        <Gauge className="w-5 h-5 text-info" />
      </div>

      {/* Content */}
      <div className="pr-14">
        <p className="text-sm font-medium uppercase text-muted-foreground">
          {t('organization.systems.stats.capacityBinding')}
        </p>
        <p className="mt-1 text-3xl font-bold tracking-tight">{displayValue}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {hasData ? (
            <>
              {periodLabel}
              <span className="ml-1 text-xs text-muted-foreground/70">
                ({t('organization.systems.stats.clickToToggle')})
              </span>
            </>
          ) : (
            t('organization.systems.stats.capacityBindingNoData')
          )}
        </p>
      </div>
    </div>
  );

  if (!hasData) {
    return cardContent;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{cardContent}</TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-2 text-sm">
            <div className="font-medium">{t('organization.systems.stats.costCalculation')}</div>
            <div className="space-y-1">
              <div className="flex justify-between gap-4">
                <span>{t('organization.systems.stats.costPerMonth')}:</span>
                <span className="font-medium">{formatCurrency(capacity.costPerMonth)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span>{t('organization.systems.stats.costPerYear')}:</span>
                <span className="font-medium">{formatCurrency(capacity.costPerYear)}</span>
              </div>
            </div>
            <div className="pt-1 border-t text-xs text-muted-foreground">
              {t('organization.systems.stats.billingRateInfo', {
                rate: formatCurrency(capacity.billingRate)
              })}
            </div>
            <div className="text-xs text-muted-foreground">
              {t('organization.systems.stats.stepsWithData', {
                count: capacity.stepsWithData,
                total: capacity.totalSteps
              })}
            </div>
            {capacity.includesManualValues === true && (
              <div className="text-xs text-muted-foreground">
                {t('processes.capacityIncludesManual')}
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export { CapacityStatCard };
